/**
 * Payment Service
 * Centralized payment logic for PayPal subscriptions and verification
 */

import { supabase } from '@/integrations/supabase/client';
import { PlanType, BillingPeriod } from '@/types/domain/user';
import { StorageKeys, sessionStorage } from './storage';

export interface PaymentFlowOptions {
  userId: string;
  email: string;
  fullName: string;
  planType: PlanType;
  billingPeriod: BillingPeriod;
  promoCode?: string;
}

export interface PayPalVerificationResult {
  success: boolean;
  isActive: boolean;
  paypalStatus?: string;
  updated?: boolean;
  error?: string;
}


/**
 * Verify PayPal subscription status
 */
export async function verifyPayPalStatus(): Promise<PayPalVerificationResult> {
  try {
    const { data, error } = await supabase.functions.invoke('verify-paypal-status');

    if (error) {
      return {
        success: false,
        isActive: false,
        error: error.message,
      };
    }

    return {
      success: true,
      isActive: data.is_active || data.paypal_status === 'ACTIVE',
      paypalStatus: data.paypal_status,
      updated: data.updated,
    };
  } catch (err: any) {
    return {
      success: false,
      isActive: false,
      error: err.message || 'Unknown error occurred',
    };
  }
}

/**
 * Clear payment flow flags
 */
export function clearPaymentFlags(): void {
  sessionStorage.remove(StorageKeys.PENDING_PAYPAL_REDIRECT);
}

/**
 * Check if payment flow is in progress
 */
export function isPaymentFlowPending(): boolean {
  return sessionStorage.getBoolean(StorageKeys.PENDING_PAYPAL_REDIRECT);
}

/**
 * Redeem promo code
 */
export async function redeemPromoCode(userId: string, promoCode: string): Promise<{ success: boolean; message?: string; error?: any }> {
  try {
    const { data, error } = await supabase.rpc('redeem_promo_code', {
      _user_id: userId,
      _code: promoCode.toUpperCase(),
    });

    if (error) {
      return { success: false, error };
    }

    if (data && data.length > 0 && data[0].success) {
      return {
        success: true,
        message: data[0].message,
      };
    }

    return {
      success: false,
      message: data?.[0]?.message || 'Invalid promo code',
    };
  } catch (err) {
    return { success: false, error: err };
  }
}
